import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { AggColumn, AggFn, InventarioRow, InventarioSortKey } from '@/types'
import { exportInventarioExcel } from '@/utils/exportInventarioExcel'
import { SortArrow } from '@/components/common/SortableHeader'
import { TableToolbar } from '@/components/common/TableToolbar'
import { AggregationMenu } from '@/components/common/AggregationMenu'
import { TableTotalsFooter } from '@/components/common/TableTotalsFooter'
import { useColumnAggregations } from '@/hooks/useColumnAggregations'
import { BarraMarcas, CabeceraMarca, FilaMarcable, MarcaCelda, useMarcasFilas } from '@/components/common/MarcasFilas'
import { claveFila } from '@/utils/marcasFilas'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'

function fmt(n: number) {
  return n.toLocaleString('es-ES', { maximumFractionDigits: 2 })
}

interface ColDef {
  key: keyof InventarioRow
  labelKey: string
  sortable?: boolean
  numeric?: boolean
  minW?: number
  defaultAgg?: AggFn
}

const COLS: ColDef[] = [
  { key: 'dataareaid',                labelKey: 'inventory.col.company',          minW: 72  },
  { key: 'articulo',                  labelKey: 'inventory.col.article',          sortable: true, minW: 100 },
  { key: 'descripcion',               labelKey: 'inventory.col.description',      sortable: true, minW: 200 },
  { key: 'familia',                   labelKey: 'inventory.col.family',           sortable: true, minW: 90  },
  { key: 'almacen',                   labelKey: 'inventory.col.warehouse',        sortable: true, minW: 90  },
  { key: 'color',                     labelKey: 'inventory.col.color',            minW: 72  },
  { key: 'talla',                     labelKey: 'inventory.col.size',             minW: 60  },
  { key: 'estilo',                    labelKey: 'inventory.col.style',            minW: 72  },
  { key: 'cantidadInventarioFisico',  labelKey: 'inventory.col.physicalStock',    sortable: true, numeric: true, minW: 90, defaultAgg: 'sum' },
  { key: 'cantidadFisicaReservada',   labelKey: 'inventory.col.physicalReserved', numeric: true, minW: 90 },
  { key: 'cantidadFisicaDisponible',  labelKey: 'inventory.col.physicalAvail',    sortable: true, numeric: true, minW: 90 },
  { key: 'cantidadEnPedido',          labelKey: 'inventory.col.onOrder',          numeric: true, minW: 90  },
  { key: 'cantidadOrdenadaReservada', labelKey: 'inventory.col.orderedReserved',  numeric: true, minW: 90 },
  { key: 'cantidadPedidoEnTotal',     labelKey: 'inventory.col.totalOrdered',     numeric: true, minW: 90 },
  { key: 'cantidadTotalDisponible',   labelKey: 'inventory.col.totalAvailable',   sortable: true, numeric: true, minW: 90, defaultAgg: 'sum' },
]

// Descriptores para los totales de columna. Se derivan de COLS para que no
// puedan desincronizarse, e incluyen también las columnas ocultas: si sólo
// entrasen las visibles, ocultar una borraría su total guardado.
const AGG_COLS: AggColumn<InventarioRow>[] = COLS
  .filter(c => c.numeric)
  .map(c => ({ key: c.key, numeric: true, decimals: 2, defaultAgg: c.defaultAgg }))

interface InventarioTableProps {
  dark: boolean
  paged: InventarioRow[]
  sorted: InventarioRow[]
  sortKey: InventarioSortKey
  sortDir: 'asc' | 'desc'
  page: number
  totalPages: number
  pageSize: number
  isLoading: boolean
  isEmpresaAll: boolean
  isAvpOnly: boolean
  setSortKey: (k: InventarioSortKey) => void
  setSortDir: (d: 'asc' | 'desc') => void
  setPage: (p: number) => void
}

export function InventarioTable({
  dark, paged, sorted, sortKey, sortDir,
  page, totalPages, pageSize, isLoading, isEmpresaAll, isAvpOnly,
  setSortKey, setSortDir, setPage,
}: InventarioTableProps) {
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()
  // Marcas del usuario. El identificador de una fila de inventario es la
  // combinación empresa + artículo + dimensiones + almacén: es lo que la vista
  // agrupa, así que dos filas nunca lo repiten y la marca sobrevive a ordenar,
  // filtrar y paginar.
  const marcas = useMarcasFilas('inventario')

  const cardBg   = dark ? 'bg-slate-900 border-white/8'  : 'bg-white border-slate-200'
  const headBg   = dark ? 'bg-slate-800/60'              : 'bg-slate-50'
  const rowHover = dark ? 'hover:bg-white/3'             : 'hover:bg-slate-50/60'
  const border   = dark ? 'border-white/6'               : 'border-slate-100'
  const txtMain  = dark ? 'text-slate-100'               : 'text-slate-800'
  const txtMut   = dark ? 'text-slate-500'               : 'text-slate-400'
  const btnBase  = 'text-[11px] px-2.5 py-1.5 rounded-lg border transition-colors font-medium cursor-pointer'
  const btnNeutral = dark
    ? `${btnBase} border-white/10 bg-white/5 text-slate-300 hover:bg-white/10`
    : `${btnBase} border-slate-200 bg-white text-slate-500 hover:bg-slate-50 shadow-sm`

  function handleExport(rows: InventarioRow[]) {
    exportInventarioExcel(rows, { isEmpresaAll, isAvpOnly, t, fmtArt })
  }

  function handleSort(key: keyof InventarioRow) {
    const sk = key as InventarioSortKey
    if (sortKey === sk) setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    else { setSortKey(sk); setSortDir('asc') }
  }

  // Translated column labels
  const colsWithLabels = COLS.map(c => ({ ...c, label: t(c.labelKey) }))

  function SortIcon({ col }: { col: typeof colsWithLabels[0] }) {
    if (!col.sortable) return null
    const active = (sortKey as keyof InventarioRow) === col.key
    return (
      <SortArrow active={active} dir={active ? sortDir : 'asc'} dark={dark} />
    )
  }

  // Los totales se calculan sobre `sorted` (todo el conjunto filtrado), no
  // sobre `paged`: cambiar de página no debe alterar la cifra.
  const { aggState, setAgg, results } = useColumnAggregations({
    storageKey: 'inventario',
    rows: sorted,
    columns: AGG_COLS,
  })

  const visibleCols = useMemo(() => {
    const hasSomething = (key: string) => sorted.some(r => r[key as keyof InventarioRow] != null && r[key as keyof InventarioRow] !== '')
    return colsWithLabels.filter(c => {
      if (['color', 'talla', 'estilo'].includes(c.key)) return hasSomething(c.key)
      // Empresa y Almacén solo visibles para usuarios con Empresa.All
      if (['dataareaid', 'almacen'].includes(c.key)) return isEmpresaAll
      return true
    })
  }, [sorted, colsWithLabels, isEmpresaAll])

  return (
    <div className={`rounded-xl border overflow-hidden shadow-sm flex flex-col ${cardBg}`}>
      {/* Toolbar */}
      <TableToolbar
        dark={dark}
        left={
          <span className={`text-[12px] font-semibold ${txtMain}`}>
            {sorted.length.toLocaleString('es-ES')} {t('common.records')}
            {sorted.length !== paged.length && (
              <span className={`ml-1.5 text-[11px] font-normal ${txtMut}`}>
                · {t('common.page')} {page}/{totalPages}
              </span>
            )}
          </span>
        }
        right={
          <button
            onClick={() => handleExport(sorted)}
            className={btnNeutral}
            title={t('common.exportExcel')}
          >
            <svg className="w-3.5 h-3.5 mr-1.5 inline-block" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12l5 3 5-3V4L8 1 3 4v8z"/>
              <path d="M8 1v14M3 4l5 3 5-3"/>
            </svg>
            {t('common.exportExcel')}
          </button>
        }
      />

      <BarraMarcas dark={dark} marcas={marcas} />

      {/* Table */}
      <div className="overflow-x-auto flex-1">
        <table className="w-full text-[12px] border-collapse">
          <thead>
            <tr className={headBg}>
              <CabeceraMarca className={`border-b ${border}`} />
              {visibleCols.map(col => (
                <th
                  key={col.key}
                  style={{ minWidth: col.minW }}
                  onClick={() => col.sortable && handleSort(col.key)}
                  className={`group text-left px-3 py-2 font-bold border-b ${border} ${
                    col.sortable ? 'cursor-pointer select-none hover:opacity-80' : ''
                  } ${col.numeric ? 'text-right' : ''} ${dark ? 'text-slate-400' : 'text-slate-500'}`}
                >
                  <span className={`inline-flex items-center gap-1 ${col.numeric ? 'flex-row-reverse' : ''}`}>
                    {col.label}
                    {col.sortable && <SortIcon col={col} />}
                    {col.numeric && (
                      <AggregationMenu
                        dark={dark}
                        colLabel={col.label}
                        active={aggState[col.key] ?? null}
                        onChange={fn => setAgg(col.key, fn)}
                      />
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && Array.from({ length: 6 }).map((_, i) => (
              <tr key={`skel-${i}`}>
                <td colSpan={visibleCols.length + 1} className="px-4 py-1.5">
                  <div className={`h-8 rounded animate-pulse ${dark ? 'bg-white/4' : 'bg-slate-100'}`}
                    style={{ opacity: 1 - i * 0.12 }} />
                </td>
              </tr>
            ))}
            {!isLoading && paged.length === 0 && (
              <tr>
                <td colSpan={visibleCols.length + 1}>
                  <div className="flex flex-col items-center justify-center py-14 px-6 text-center">
                    <svg className={`w-8 h-8 mb-3 opacity-30 ${txtMut}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 12h-6l-2 3H10l-2-3H2"/>
                      <path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z"/>
                    </svg>
                    <p className={`text-[13px] font-semibold ${dark ? 'text-slate-100' : 'text-slate-900'}`}>
                      {t('inventory.noResultsFilters')}
                    </p>
                  </div>
                </td>
              </tr>
            )}
            {!isLoading && paged.map((row, i) => {
              const clave = claveFila(
                row.dataareaid, row.articulo, row.color, row.talla, row.estilo, row.almacen,
              )
              return (
              <FilaMarcable
                key={`${clave}-${i}`}
                marcas={marcas}
                clave={clave}
                dark={dark}
                className={`border-b last:border-0 ${border} ${rowHover} transition-colors`}
              >
                <MarcaCelda marcas={marcas} clave={clave} dark={dark} />
                {visibleCols.map(col => {
                  const raw = row[col.key as keyof InventarioRow]
                  const isNum = col.numeric && typeof raw === 'number'
                  const rawDisplay = isNum ? fmt(raw as number) : (raw ?? '—')
                  const display = col.key === 'articulo'
                    ? fmtArt(raw as string)
                    : rawDisplay
                  const isZero  = isNum && (raw as number) === 0
                  return (
                    <td
                      key={col.key}
                      className={`px-3 py-2 ${col.numeric ? 'text-right tabular-nums' : ''} ${
                        isZero
                          ? (dark ? 'text-slate-600' : 'text-slate-300')
                          : col.key === 'cantidadFisicaDisponible' && (raw as number) > 0
                            ? (dark ? 'text-emerald-400 font-semibold' : 'text-emerald-600 font-semibold')
                            : txtMain
                      }`}
                    >
                      {String(display)}
                    </td>
                  )
                })}
              </FilaMarcable>
              )
            })}
          </tbody>
          {!isLoading && paged.length > 0 && (
            <TableTotalsFooter
              dark={dark}
              columns={[
                // El canalón de marcas es una columna más para el pie: sin ella
                // la rejilla del `tfoot` se desplazaría respecto a la cabecera.
                { key: '__marca__', align: 'left' as const },
                ...visibleCols.map(c => ({
                  key: c.key,
                  decimals: 2,
                  align: (c.numeric ? 'right' : 'left') as 'right' | 'left',
                })),
              ]}
              results={results}
              rowCount={sorted.length}
            />
          )}
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={`flex items-center justify-between px-4 py-2.5 border-t ${border}`}>
          <span className={`text-[11px] ${txtMut}`}>
            {Math.min((page - 1) * pageSize + 1, sorted.length)}–{Math.min(page * pageSize, sorted.length)} {t('common.of')} {sorted.length}
          </span>
          <div className="flex items-center gap-1.5">
            <button onClick={() => setPage(1)}            disabled={page === 1}          className={`${btnNeutral} disabled:opacity-40 disabled:cursor-not-allowed`}>«</button>
            <button onClick={() => setPage(page - 1)}     disabled={page === 1}          className={`${btnNeutral} disabled:opacity-40 disabled:cursor-not-allowed`}>‹</button>
            <span className={`text-[11px] px-2 ${txtMut}`}>{page} / {totalPages}</span>
            <button onClick={() => setPage(page + 1)}     disabled={page === totalPages} className={`${btnNeutral} disabled:opacity-40 disabled:cursor-not-allowed`}>›</button>
            <button onClick={() => setPage(totalPages)}   disabled={page === totalPages} className={`${btnNeutral} disabled:opacity-40 disabled:cursor-not-allowed`}>»</button>
          </div>
        </div>
      )}
    </div>
  )
}
